import { toBase64 } from 'lib0/buffer';

import { DocLimitError, DocNotConvertedError, DocTooLargeError } from '@/doc-sync/doc-store';
import { goneError, planRefusal, RemoteDocStore, type RemoteDocMeta } from '@/doc-sync/remote-doc-store';
import type { TokenProvider } from '@/doc-sync/auth-identity';

import type { DiagramMetaExtra } from './diagram-store';
import { parseStoredDiagram, type StoredDiagram } from './stored-diagram';

export type { TokenProvider };

export type RemoteDiagramMeta = RemoteDocMeta<DiagramMetaExtra>;

/** The diagram slice of a gallery row, from a server list entry. A server that
 *  predates archiving sends no flag: that reads as not archived. */
export function diagramMetaExtraFromRemote(r: RemoteDiagramMeta): DiagramMetaExtra {
    return { kind: r.kind, archived: r.archived === true };
}

/** Mirror of the server's MAX_DOC_BYTES whole-body cap. */
export const MAX_DIAGRAM_BODY_BYTES = 1_000_000;

/** Outcome of POST /api/diagrams/:id/seed (SP-52 wire contract). */
export type SeedResult =
    | { kind: 'ok'; revision: number }
    | { kind: 'already-seeded'; currentRevision: number | null }
    | { kind: 'revision-mismatch'; currentRevision: number | null }
    | { kind: 'invalid' }
    | { kind: 'forbidden' }
    | { kind: 'not-found' }
    | { kind: 'deleted' }
    | { kind: 'too-large' }
    /** 423: the diagram is archived, so it is not converted now. */
    | { kind: 'archived' };

/** HTTP client for /api/diagrams. Fetches a FRESH Clerk token per request
 *  (they expire in ~60s). Throws DiagramConflictError on 409 and
 *  RemoteUnreachableError on network failure / missing token / 401·403 —
 *  the two signals SyncedDiagramStore branches on. */
export class RemoteDiagramStore extends RemoteDocStore<StoredDiagram, DiagramMetaExtra> {
    constructor(opts: { getToken: TokenProvider; baseUrl?: string; fetchFn?: typeof fetch }) {
        super({
            ...opts,
            basePath: '/api/diagrams',
            listKey: 'diagrams',
            docKey: 'diagram',
            parse: parseStoredDiagram,
            maxPutBodyBytes: MAX_DIAGRAM_BODY_BYTES,
            tooLargeMessage: 'diagram too large to sync',
        });
    }

    /** Converts a legacy diagram to Yjs (SP-52): `update` is the full Yjs state
     *  built from the JSON at exactly `revision`. A 403 here is a backend that
     *  still gates conversion (`collab_sync`, before SP-109), not an auth
     *  failure — hence `allowForbidden`; the session falls back to legacy. */
    async seed(id: string, revision: number, update: Uint8Array): Promise<SeedResult> {
        const res = await this.request(`/api/diagrams/${id}/seed`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ revision, update: toBase64(update) }),
        }, { allowForbidden: true });
        if (res.ok) return { kind: 'ok', revision: ((await res.json()) as { revision: number }).revision };
        if (res.status === 400) return { kind: 'invalid' };
        if (res.status === 403) return { kind: 'forbidden' };
        if (res.status === 404) return { kind: 'not-found' };
        if (res.status === 410) return { kind: 'deleted' };
        if (res.status === 413) return { kind: 'too-large' };
        if (res.status === 423) return { kind: 'archived' };
        if (res.status === 409) {
            const body = (await res.json().catch(() => ({}))) as { error?: string; currentRevision?: unknown };
            const currentRevision = typeof body.currentRevision === 'number' ? body.currentRevision : null;
            return body.error === 'revision-mismatch'
                ? { kind: 'revision-mismatch', currentRevision }
                : { kind: 'already-seeded', currentRevision };
        }
        throw new Error(`POST /api/diagrams/${id}/seed failed: ${res.status}`);
    }

    /** Offers the JSON projection of a Yjs-synced diagram. The server keeps it
     *  only if `stateVector` shows this client has everything the room has. */
    async projection(doc: StoredDiagram, stateVector: Uint8Array): Promise<{ accepted: boolean }> {
        const body = JSON.stringify({ stateVector: toBase64(stateVector), diagram: doc });
        if (new TextEncoder().encode(body).length > MAX_DIAGRAM_BODY_BYTES) {
            throw new DocTooLargeError('diagram too large to sync');
        }
        const res = await this.request(`/api/diagrams/${doc.id}/projection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
        });
        if (res.ok) {
            const parsed = (await res.json().catch(() => ({}))) as { accepted?: unknown };
            return { accepted: parsed.accepted === true };
        }
        if (res.status === 410) throw await goneError(res);
        if (res.status === 413) throw new DocTooLargeError('diagram too large to sync');
        if (res.status === 409) {
            const parsed = (await res.json().catch(() => ({}))) as { sync?: unknown };
            if (parsed.sync === 'legacy') throw new DocNotConvertedError();
        }
        throw new Error(`POST /api/diagrams/${doc.id}/projection failed: ${res.status}`);
    }

    /** Freezes a diagram: it stops counting against the plan's active limit and
     *  becomes read-only on the server. Touches nothing on this device. A 404
     *  (an id the server has never seen, e.g. a device-only copy) is a plain
     *  failure for the caller to word. */
    async archive(id: string): Promise<void> {
        const res = await this.request(`/api/diagrams/${id}/archive`, { method: 'POST' });
        if (!res.ok) throw new Error(`POST /api/diagrams/${id}/archive failed: ${res.status}`);
    }

    /** Takes a diagram out of the archive. The server answers 402 when there is
     *  no free active slot — a verdict, not a failure, so it comes back as the
     *  `limit` for the caller's upgrade prompt. */
    async unarchive(id: string): Promise<'unarchived' | { limit: number }> {
        const res = await this.request(`/api/diagrams/${id}/unarchive`, { method: 'POST' });
        if (res.status === 402) {
            const refusal = await planRefusal(res);
            if (refusal instanceof DocLimitError) return { limit: refusal.limit };
        }
        if (!res.ok) throw new Error(`POST /api/diagrams/${id}/unarchive failed: ${res.status}`);
        return 'unarchived';
    }
}
